import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';




import { DEFAULT_THEME_ID, THEME_OPTION_IDS, THEME_OPTIONS } from '../theme/themes';



const AUTH_TOKEN_KEY = 'forc.auth.token';

function getStoredAuthToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}


const THEME_STORAGE_KEY = 'forc.ui.theme';

function getInitialTheme() {
  if (typeof window === 'undefined') return DEFAULT_THEME_ID;
  const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (savedTheme && THEME_OPTION_IDS.has(savedTheme)) {
    return savedTheme;
  }
  return DEFAULT_THEME_ID;
}

// Apply theme synchronously at module load — prevents any flash of wrong theme
const _initial = getInitialTheme();
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('data-theme', _initial);
  document.documentElement.style.colorScheme = _initial === 'deep' ? 'dark' : 'light';
}

const ThemeContext = createContext({
  themeId: DEFAULT_THEME_ID,
  setThemeId: () => {},
  applyUserTheme: () => {},
  options: THEME_OPTIONS,
});

export function ThemeProvider({ children }) {
  const [themeId, setThemeIdState] = useState(getInitialTheme);
  const saveTimerRef = useRef(null);
  const isFirstRender = useRef(true);

  // Stable function — setThemeIdState is always the same reference from useState
  const setThemeId = useCallback((nextThemeId) => {
    if (!THEME_OPTION_IDS.has(nextThemeId)) return;
    setThemeIdState(nextThemeId);
  }, []);

  // Stable function — used by AuthContext after login/session restore
  const applyUserTheme = useCallback((user) => {
    if (user?.theme && THEME_OPTION_IDS.has(user.theme)) {
      setThemeIdState(user.theme);
    }
  }, []);

  // Apply data-theme to <html> on every change
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeId);
    document.documentElement.style.colorScheme = themeId === 'deep' ? 'dark' : 'light';
    window.localStorage.setItem(THEME_STORAGE_KEY, themeId);
  }, [themeId]);

  // Debounced backend save — skip the very first run (page load) so we don't
  // immediately write before the user makes an intentional change
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    const isPublicAuthRoute = pathname === '/login'
      || pathname === '/forgot-password'
      || pathname === '/reset-password'
      || pathname === '/accept-invite'
      || pathname === '/bootstrap-admin';
    if (isPublicAuthRoute) {
      return;
    }
    if (!getStoredAuthToken()) {
      return;
    }
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {

      const Factory = require( '../records/auth' );

      Factory.updateMyPreferences({ theme: themeId }).catch(() => {
        // Fail silently — local preference is still applied regardless
      });
    }, 800);
    return () => clearTimeout(saveTimerRef.current);
  }, [themeId]);

  const value = useMemo(
    () => ({
      themeId,
      setThemeId,
      applyUserTheme,
      options: THEME_OPTIONS,
    }),
    [themeId, setThemeId, applyUserTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
